<!--
  printer-test.component.html
  Dev/testing harness for the print API (POST {apiUrl}/V1/print).
  Build any PrintRequest combo, submit it, and inspect the per-document outcomes.
-->

<ion-header>
  <ion-toolbar>
    <ion-title>Printer Testing</ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="close()">
        <ion-icon name="close"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content class="ion-padding">

  <!-- Printer source: current-facility defaults + optional live override list -->
  <ion-card>
    <ion-card-content>
      <ion-text><h3>Printer source</h3></ion-text>
      <p class="hint">
        Printer names default to the current facility (<strong>{{ locationLabel }}</strong>).
        <ng-container *ngIf="!locationHasDefaults">
          This facility has no default printers configured — using the sample values below.
        </ng-container>
        Fetch the live printer list to override them.
      </p>

      <ion-button
        size="small"
        fill="outline"
        [disabled]="printersLoading"
        (click)="loadPrinters()">
        <ion-spinner *ngIf="printersLoading" slot="start" name="crescent"></ion-spinner>
        {{ printersLoading ? 'Loading…' : 'Load available printers' }}
      </ion-button>
      <p class="hint" *ngIf="printersLoaded && printers.length === 0">
        No printers reported by the API — printer-name fields stay manual.
      </p>
      <p class="hint" *ngIf="printers.length > 0">
        {{ printers.length }} printer(s) loaded — choose one from the override dropdowns below.
      </p>
    </ion-card-content>
  </ion-card>

  <!-- Request form -->
  <ion-card>
    <ion-card-content>
      <ion-list lines="full">

        <ion-item>
          <ion-label>Kind</ion-label>
          <ion-select [(ngModel)]="request.kind" interface="popover">
            <ion-select-option value="Zebra">Zebra</ion-select-option>
            <ion-select-option value="Copier">Copier</ion-select-option>
            <ion-select-option value="Both">Both</ion-select-option>
          </ion-select>
        </ion-item>

        <!-- Zebra fields -->
        <ng-container *ngIf="request.kind === 'Zebra' || request.kind === 'Both'">
          <ion-item>
            <ion-label position="stacked">Zebra printer name <ion-text color="danger">*</ion-text></ion-label>
            <ion-input [(ngModel)]="request.zebraPrinterName" placeholder="SHOP-ZEBRA-01"></ion-input>
          </ion-item>
          <ion-item *ngIf="printers.length > 0">
            <ion-label>Override from list</ion-label>
            <ion-select [(ngModel)]="request.zebraPrinterName" interface="popover" placeholder="Choose printer">
              <ion-select-option *ngFor="let printer of printers" [value]="printer.name">
                {{ printer.name }}{{ printer.isOnline ? '' : ' (offline)' }}
              </ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Job number <ion-text color="danger">*</ion-text></ion-label>
            <ion-input [(ngModel)]="request.jobNumber" placeholder="12345"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Label count (1–100)</ion-label>
            <ion-input type="number" min="1" max="100" [(ngModel)]="request.labelCount"></ion-input>
          </ion-item>
        </ng-container>

        <!-- Copier fields -->
        <ng-container *ngIf="request.kind === 'Copier' || request.kind === 'Both'">
          <ion-item>
            <ion-label position="stacked">Copier printer name <ion-text color="danger">*</ion-text></ion-label>
            <ion-input [(ngModel)]="request.copierPrinterName" placeholder="SHOP-COPIER-02"></ion-input>
          </ion-item>
          <ion-item *ngIf="printers.length > 0">
            <ion-label>Override from list</ion-label>
            <ion-select [(ngModel)]="request.copierPrinterName" interface="popover" placeholder="Choose printer">
              <ion-select-option *ngFor="let printer of printers" [value]="printer.name">
                {{ printer.name }}{{ printer.isOnline ? '' : ' (offline)' }}
              </ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">PO number <ion-text color="danger">*</ion-text></ion-label>
            <ion-input [(ngModel)]="request.poNumber" placeholder="PO-9981"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Copy count (1–100)</ion-label>
            <ion-input type="number" min="1" max="100" [(ngModel)]="request.copyCount"></ion-input>
          </ion-item>
        </ng-container>

        <!-- Optional / shared fields -->
        <ion-item>
          <ion-label position="stacked">Vendor</ion-label>
          <ion-input [(ngModel)]="request.vendor"></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Job name</ion-label>
          <ion-input [(ngModel)]="request.jobName"></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Contact (also Zebra "Tech")</ion-label>
          <ion-input [(ngModel)]="request.contact"></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Qty units (string, e.g. "5")</ion-label>
          <ion-input [(ngModel)]="request.qtyUnits"></ion-input>
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Date in (ISO or blank)</ion-label>
          <ion-input [(ngModel)]="request.dateIn" placeholder="2026-06-17"></ion-input>
        </ion-item>

        <!-- Copier-only optional fields -->
        <ng-container *ngIf="request.kind === 'Copier' || request.kind === 'Both'">
          <ion-item>
            <ion-label position="stacked">JC dept</ion-label>
            <ion-input [(ngModel)]="request.jcDept"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Type</ion-label>
            <ion-input [(ngModel)]="request.type"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Shelf location</ion-label>
            <ion-input [(ngModel)]="request.shelfLocation"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Shop sq ft</ion-label>
            <ion-input type="number" [(ngModel)]="request.shopSqFt"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Estimated date out (ISO or blank)</ion-label>
            <ion-input [(ngModel)]="request.estimatedDateOut" placeholder="2026-07-01"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Notify</ion-label>
            <ion-input [(ngModel)]="request.notify"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Items</ion-label>
            <ion-input [(ngModel)]="request.items"></ion-input>
          </ion-item>
          <ion-item>
            <ion-label position="stacked">Notes</ion-label>
            <ion-input [(ngModel)]="request.notes"></ion-input>
          </ion-item>
   
        </ng-container>

      </ion-list>

      <ion-button expand="block" color="primary" class="ion-margin-top" [disabled]="isSubmitting" (click)="submit()">
        <ion-spinner *ngIf="isSubmitting" slot="start" name="crescent"></ion-spinner>
        <ion-icon *ngIf="!isSubmitting" slot="start" name="print-outline"></ion-icon>
        {{ isSubmitting ? 'Sending…' : 'Send test print' }}
      </ion-button>
    </ion-card-content>
  </ion-card>

  <!-- Request preview -->
  <ion-card>
    <ion-card-content>
      <ion-text><h3>Request body</h3></ion-text>
      <pre class="code-block">{{ requestPreview }}</pre>
    </ion-card-content>
  </ion-card>

  <!-- Error (request itself failed: non-200 / network) -->
  <ion-card *ngIf="errorMessage" class="result-error">
    <ion-card-content>
      <ion-text color="danger"><h3>Request failed</h3></ion-text>
      <p>{{ errorMessage }}</p>
    </ion-card-content>
  </ion-card>

  <!-- Result (200 OK — inspect overallSuccess + per-document outcomes) -->
  <ion-card *ngIf="result">
    <ion-card-content>
      <ion-text>
        <h3>
          Result —
          <ion-text [color]="result.overallSuccess ? 'success' : 'warning'">
            {{ result.overallSuccess ? 'overall success' : 'partial / failed' }}
          </ion-text>
        </h3>
      </ion-text>

      <ion-list lines="full">
        <ion-item *ngFor="let outcome of result.outcomes">
          <ion-icon
            slot="start"
            [name]="outcome.success ? 'checkmark-circle-outline' : 'close-circle-outline'"
            [color]="outcome.success ? 'success' : 'danger'">
          </ion-icon>
          <ion-label class="ion-text-wrap">
            <h4>{{ outcome.kind }} — HTTP {{ outcome.statusCode }}</h4>
            <p>{{ outcome.message }}</p>
          </ion-label>
        </ion-item>
      </ion-list>
    </ion-card-content>
  </ion-card>

</ion-content>
